import React, { useState, useMemo } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import PageWithHeader from '../common/PageWithHeader';
import LoadingCover from '../common/LoadingCover';
import { useAppSelector } from '../../redux/hooks';
import { 
  useGetFeedbackSurveyMySubmissionsQuery, 
  useGetUsersQuery,
  FeedbackSurveyWithRelations,
} from '../../redux/vmsApi';
import { useRouter } from '../../hooks/useRouter';

// Helper to get status color
const getStatusColor = (status?: string): 'default' | 'warning' | 'success' | 'info' | 'error' => {
  switch (status?.toLowerCase()) {
    case 'draft':
      return 'default';
    case 'submitted':
      return 'success';
    default:
      return 'default';
  }
};

// Helper to format date
const formatDate = (dateStr?: string): string => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

// Calculate average rating from responses
const calculateAverageRating = (survey: FeedbackSurveyWithRelations): number => {
  const ratings = survey.responses
    .filter(r => r.responseRating !== undefined)
    .map(r => r.responseRating!);
  if (ratings.length === 0) return 0;
  return ratings.reduce((sum, r) => sum + r, 0) / ratings.length;
};

// Get color based on rating
const getRatingColor = (rating: number): string => {
  if (rating >= 9) return '#1b5e20';
  if (rating >= 8) return '#2e7d32';
  if (rating >= 7) return '#4caf50';
  if (rating >= 6) return '#8bc34a';
  if (rating >= 5) return '#ffc107';
  if (rating >= 4) return '#ff9800';
  if (rating >= 3) return '#ff5722';
  if (rating >= 2) return '#f44336';
  return '#d32f2f';
};

const My360Submissions: React.FC = () => {
  const router = useRouter();
  const currentUser = useAppSelector((state) => state.auth.user);
  const { data: submissions, isLoading, error } = useGetFeedbackSurveyMySubmissionsQuery();
  const { data: users } = useGetUsersQuery();
  const [selectedPeriod, setSelectedPeriod] = useState<string>('');

  // Get user name by ID
  const getUserName = (userId: string): string => {
    const user = users?.find(u => u.id === userId);
    return user?.name || 'Unknown User';
  };

  // Get unique periods from submissions
  const availablePeriods = useMemo(() => {
    if (!submissions) return [];
    const periods = [...new Set(submissions.map(s => s.reviewPeriod))];
    return periods.sort().reverse();
  }, [submissions]);

  // Filter submissions by selected period
  const filteredSubmissions = useMemo(() => {
    if (!submissions) return [];
    if (!selectedPeriod) return submissions;
    return submissions.filter(s => s.reviewPeriod === selectedPeriod);
  }, [submissions, selectedPeriod]);

  // Group submissions by status
  const { drafts, submitted } = useMemo(() => {
    const drafts: FeedbackSurveyWithRelations[] = [];
    const submitted: FeedbackSurveyWithRelations[] = [];
    
    filteredSubmissions.forEach(s => {
      if (s.status === 'draft') {
        drafts.push(s);
      } else {
        submitted.push(s);
      }
    });
    
    return { drafts, submitted };
  }, [filteredSubmissions]);

  if (isLoading) return <LoadingCover />;

  if (error) {
    return (
      <PageWithHeader>
        <Box sx={{ maxWidth: 1400, mx: 'auto', p: 3 }}>
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="error">Error loading 360 feedback submissions</Typography>
            <Button
              variant="contained"
              startIcon={<ArrowBackIcon />}
              onClick={() => router.push('/reviews/360-degree-survey')}
              sx={{ mt: 2 }}
            >
              Back to Survey
            </Button>
          </Paper>
        </Box>
      </PageWithHeader>
    );
  }

  return (
    <PageWithHeader>
      <Box sx={{ maxWidth: 1400, mx: 'auto', p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={() => router.push('/reviews')}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4">My 360° Feedback Surveys</Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => router.push('/reviews/360-degree-survey')}
          >
            New Survey
          </Button>
        </Box>

        {/* Summary Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h3" color="primary">
                  {submissions?.length || 0}
                </Typography>
                <Typography color="text.secondary">Total Surveys</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h3" color="success.main">
                  {submissions?.filter(s => s.status === 'submitted').length || 0}
                </Typography>
                <Typography color="text.secondary">Submitted</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h3" color="text.secondary">
                  {submissions?.filter(s => s.status === 'draft').length || 0}
                </Typography>
                <Typography color="text.secondary">Drafts</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Filter */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Filter by Period</InputLabel>
            <Select
              value={selectedPeriod}
              label="Filter by Period"
              onChange={(e: SelectChangeEvent) => setSelectedPeriod(e.target.value)}
            >
              <MenuItem value="">All Periods</MenuItem>
              {availablePeriods.map(period => (
                <MenuItem key={period} value={period}>{period}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Paper>

        {/* Drafts Section */}
        {drafts.length > 0 && (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip label="Drafts" color="default" size="small" />
              Continue where you left off
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Employee</TableCell>
                    <TableCell>Period</TableCell>
                    <TableCell>Last Saved</TableCell>
                    <TableCell>Progress</TableCell>
                    <TableCell align="right">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {drafts.map(survey => {
                    const avgRating = calculateAverageRating(survey);
                    const avgPercent = (avgRating / 10) * 100;
                    return (
                      <TableRow key={survey.id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PersonIcon fontSize="small" color="action" />
                            {getUserName(survey.userId)}
                          </Box>
                        </TableCell>
                        <TableCell>{survey.reviewPeriod}</TableCell>
                        <TableCell>{formatDate(survey.lastSavedAt)}</TableCell>
                        <TableCell>
                          <Chip 
                            label={`${avgPercent.toFixed(0)}%`}
                            size="small"
                            sx={{ backgroundColor: getRatingColor(avgRating), color: 'white' }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => router.push('/reviews/360-degree-survey')}
                          >
                            Continue
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        {/* Submitted Section */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip label="Submitted" color="success" size="small" />
            Completed Surveys
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          {submitted.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="text.secondary">
                No submitted surveys yet.
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                sx={{ mt: 2 }}
                onClick={() => router.push('/reviews/360-degree-survey')}
              >
                Create Your First Survey
              </Button>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Employee</TableCell>
                    <TableCell>Period</TableCell>
                    <TableCell>Submitted</TableCell>
                    <TableCell>Overall Rating</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {submitted.map(survey => {
                    const avgRating = calculateAverageRating(survey);
                    const avgPercent = (avgRating / 10) * 100;
                    return (
                      <TableRow key={survey.id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PersonIcon fontSize="small" color="action" />
                            {getUserName(survey.userId)}
                          </Box>
                        </TableCell>
                        <TableCell>{survey.reviewPeriod}</TableCell>
                        <TableCell>{formatDate(survey.lastSavedAt)}</TableCell>
                        <TableCell>
                          <Chip 
                            label={`${avgPercent.toFixed(0)}%`}
                            size="small"
                            sx={{ backgroundColor: getRatingColor(avgRating), color: 'white' }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={survey.status || 'submitted'}
                            color={getStatusColor(survey.status)}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>

        {/* Empty state */}
        {!submissions || submissions.length === 0 ? (
          <Paper sx={{ p: 6, textAlign: 'center', mt: 3 }}>
            <PersonIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              No 360° Feedback Surveys Yet
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              Start giving feedback to your colleagues by creating a new survey.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => router.push('/reviews/360-degree-survey')}
            >
              Create New Survey
            </Button>
          </Paper>
        ) : null}
      </Box>
    </PageWithHeader>
  );
};

export default My360Submissions;
